/**
 * Document designs.
 *
 * The base design is the case's DocumentSettings row (flat ColumnSettings + text fields); it is
 * used when a document selects no version.
 * Named versions are DocumentDesignVersions rows with the same fields (own theme, colors,
 * header, texts...). Several versions may share one theme.
 *
 * In the settings dialog both are edited through the same `settings` state:
 *   { customDescription, customFooter, headerRightText, headerLeftText, itemsDisplayMode,
 *     columnSettings: { ...visual keys, headerDetails, pageBackgroundImage(url), pageBackgroundImageKey } }
 */
import {
    normalizeColumnSettings,
    normalizeHeaderDetails,
} from './documentSettingsDefaults';
import { resolveDocumentStyle } from './documentStyleVersions';

/** Target id of the base design in the dialog (versions use their row id). */
export const BASE_DESIGN_ID = null;

// Keys that are never part of a design's visual settings
const CONTAINER_KEYS = ['designVersions', 'activeDesignVersionId', 'headerDetails', 'pageBackgroundImageUrls'];

const generateHeaderFieldId = (type) => `${type}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Visual column settings to persist: drops container keys and keeps the stable S3 key for the
 * page background instead of an expiring pre-signed URL.
 */
export const extractVisualColumnSettings = (columnSettings = {}) => {
    const visual = { ...columnSettings };
    CONTAINER_KEYS.forEach((key) => { delete visual[key]; });
    if (visual.pageBackgroundImageKey !== undefined) {
        visual.pageBackgroundImage = visual.pageBackgroundImageKey || '';
        delete visual.pageBackgroundImageKey;
    }
    return visual;
};

/**
 * Default header labels; callers pass translated labels when available.
 */
export const getDefaultHeaderLabels = (t, direction = 'rtl') => {
    const isLTR = direction === 'ltr';
    const tr = (key, he, en) => {
        const value = typeof t === 'function' ? t(key) : '';
        return value && value !== key ? value : (isLTR ? en : he);
    };
    return {
        businessName: '',
        taxId: tr('HeaderFieldTaxIdLabel', 'ח"פ/ע"מ', 'Company ID'),
        address: tr('HeaderFieldAddressLabel', 'כתובת', 'Address'),
        phone: tr('HeaderFieldPhoneLabel', 'טל1', 'Phone 1'),
        phone2: tr('HeaderFieldPhone2Label', 'טל2', 'Phone 2'),
        email: '',
        website: '',
    };
};

/**
 * Seed header details from the business (client) record. The result is a copy — editing it
 * never touches the client record.
 */
export const buildHeaderDetailsFromClient = (client = {}, labels = getDefaultHeaderLabels()) => {
    const c = client || {};
    const values = {
        businessName: c.name || '',
        taxId: (c.vatClientNumber && String(c.vatClientNumber).trim()) || c.caseId || '',
        address: c.location || '',
        phone: c.phoneNumber || '',
        phone2: c.secondaryPhoneNumber || '',
        email: c.email || '',
        website: c.website || '',
    };
    return {
        fields: Object.keys(values).map((type) => ({
            id: type,
            type,
            label: labels[type] ?? '',
            value: values[type],
            visible: true,
            joinPrevious: type === 'phone2',
        })),
    };
};

export const createCustomHeaderField = (label = '', value = '') => ({
    id: generateHeaderFieldId('custom'),
    type: 'custom',
    label,
    value,
    visible: true,
    joinPrevious: false,
});

export const getStyleDisplayName = (styleId, t) => {
    const style = resolveDocumentStyle(styleId);
    const translated = typeof t === 'function' ? t(style.nameKey) : '';
    return translated && translated !== style.nameKey ? translated : style.nameFallback;
};

/** "Name · Theme" label for version pickers (just the theme when the name repeats it). */
export const getDesignVersionLabel = (version, t) => {
    const layout = getStyleDisplayName(version?.documentStyleVersion, t);
    const name = version?.name?.trim();
    return name && name !== layout ? `${name} · ${layout}` : layout;
};

/** Number of saved versions, not counting the base design (entries without an id). */
export const countDesignVersions = (versions) =>
    Array.isArray(versions) ? versions.filter((v) => v && v.id != null && v.id !== '').length : 0;

/** Minimum number of saved versions (besides the base design) before the document picker is shown. */
export const MIN_VERSIONS_FOR_PICKER = 1;

/** The create-document picker is useful only when there is a real choice beyond the base design. */
export const shouldShowDesignVersionPicker = (versions) =>
    countDesignVersions(versions) >= MIN_VERSIONS_FOR_PICKER;

/**
 * Dialog state from a stored design (DocumentSettings response or DocumentDesignVersions row,
 * both already carrying a pre-signed `pageBackgroundImage` + raw `pageBackgroundImageKey`).
 * `fallbackHeaderDetails` seeds the header when the design has none yet.
 */
export const designToSettings = (design = {}, fallbackHeaderDetails = null) => {
    const raw = design?.columnSettings || {};
    const visual = normalizeColumnSettings(raw);
    CONTAINER_KEYS.forEach((key) => { delete visual[key]; });
    const bgUrl = raw.pageBackgroundImage || '';
    const isUrl = /^(https?:|data:|blob:)/i.test(bgUrl);
    return {
        customDescription: design?.constantClientDescription || '',
        customFooter: design?.constantInvoiceDescription || '',
        headerRightText: design?.headerRightText || '',
        headerLeftText: design?.headerLeftText || '',
        itemsDisplayMode: design?.itemsDisplayMode || '',
        columnSettings: {
            ...visual,
            headerDetails: normalizeHeaderDetails(raw.headerDetails || fallbackHeaderDetails),
            pageBackgroundImage: bgUrl,
            pageBackgroundImageKey: raw.pageBackgroundImageKey ?? (isUrl ? '' : bgUrl),
        },
    };
};

/** Persistable fields of the dialog's editing state (shared by base design and versions). */
export const settingsToDesignPayload = (settings = {}) => {
    const columnSettings = settings.columnSettings || {};
    return {
        documentStyleVersion: columnSettings.documentStyleVersion || 'classic',
        constantClientDescription: settings.customDescription || null,
        constantInvoiceDescription: settings.customFooter || null,
        headerRightText: settings.headerRightText || null,
        headerLeftText: settings.headerLeftText || null,
        itemsDisplayMode: settings.itemsDisplayMode || null,
        columnSettings: {
            ...extractVisualColumnSettings(columnSettings),
            headerDetails: normalizeHeaderDetails(columnSettings.headerDetails) || null,
        },
    };
};

/** UpdateDocumentSettings payload for the base design. */
export const buildDocumentSettingsPayload = (settings = {}) => {
    const { documentStyleVersion, ...payload } = settingsToDesignPayload(settings);
    return payload;
};

/** Stored design row in the dialog's version list (keeps the presigned background for preview). */
export const designRowFromSettings = (row, settings) => ({
    ...row,
    ...settingsToDesignPayload(settings),
    columnSettings: {
        ...settingsToDesignPayload(settings).columnSettings,
        pageBackgroundImage: settings.columnSettings?.pageBackgroundImage || '',
        pageBackgroundImageKey: settings.columnSettings?.pageBackgroundImageKey || '',
    },
});
